import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  Truck, Home as HomeIcon, Bike, Package, Warehouse, Construction,
  Users, CalendarCheck, MessageCircle, Settings2, ArrowRight, ArrowUpRight,
  Star, Check, MapPin, Zap, Rocket, Route,
} from "lucide-react";
import {
  SERVICES, STATS, WHY, STEPS, MOVING_ITEMS, BUSINESS_SECTORS,
  ROUTES_LIST, REVIEWS, IMAGES, waLink, GOOGLE_REVIEWS_URL,
} from "@/data/site";
import { useQuote } from "@/components/QuoteDialog";
import { Reveal } from "@/components/Reveal";

const ICONS = { Truck, Home: HomeIcon, Bike, Package, Warehouse, Construction, Users, CalendarCheck, MessageCircle, Settings2 };
const Container = ({ children, className = "" }) => (
  <div className={`max-w-[1440px] mx-auto px-5 md:px-10 ${className}`}>{children}</div>
);
const Overline = ({ children }) => (
  <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#FF5A00]">{children}</p>
);

// ---------------------------------------------------------- Quick quote
export const QuickQuote = () => {
  const { openQuote } = useQuote();
  const [f, setF] = useState({ partenza: "", destinazione: "", servizio: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const input = "w-full h-14 px-5 rounded-xl bg-zinc-50 border border-zinc-200 outline-none transition-colors focus:border-[#FF5A00] focus:bg-white text-[15px] placeholder:text-zinc-400";
  return (
    <Container className="relative -mt-24 md:-mt-28 z-30">
      <Reveal>
        <div className="bg-white rounded-[1.75rem] shadow-[0_30px_60px_-15px_rgba(0,0,0,0.18)] border border-zinc-100 p-6 md:p-9" data-testid="quick-quote">
          <div className="flex items-center gap-2 mb-5">
            <MapPin className="text-[#FF5A00]" size={20} />
            <h3 className="font-head text-xl md:text-2xl font-extrabold tracking-tight">Dove dobbiamo trasportare?</h3>
          </div>
          <div className="grid md:grid-cols-4 gap-3">
            <div className="md:col-span-1">
              <label className="text-[11px] uppercase tracking-wider font-bold text-zinc-400 ml-1">Partenza</label>
              <input className={`${input} mt-1.5`} placeholder="Città / CAP" value={f.partenza} onChange={set("partenza")} data-testid="qq-partenza" />
            </div>
            <div className="md:col-span-1">
              <label className="text-[11px] uppercase tracking-wider font-bold text-zinc-400 ml-1">Destinazione</label>
              <input className={`${input} mt-1.5`} placeholder="Città / CAP" value={f.destinazione} onChange={set("destinazione")} data-testid="qq-destinazione" />
            </div>
            <div className="md:col-span-1">
              <label className="text-[11px] uppercase tracking-wider font-bold text-zinc-400 ml-1">Cosa trasportare</label>
              <select className={`${input} mt-1.5 text-zinc-600`} value={f.servizio} onChange={set("servizio")} data-testid="qq-servizio">
                <option value="">Seleziona</option>
                <option value="trasloco">Trasloco</option>
                <option value="moto">Moto / Scooter</option>
                <option value="mobilio">Mobilio</option>
                <option value="merci">Merci</option>
                <option value="altro">Altro</option>
              </select>
            </div>
            <div className="md:col-span-1 flex items-end">
              <button onClick={() => openQuote(f)} data-testid="qq-submit"
                className="w-full h-14 rounded-xl bg-[#FF5A00] text-white font-bold flex items-center justify-center gap-2 hover:bg-[#E04F00] transition-colors">
                Richiedi preventivo <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </Reveal>
    </Container>
  );
};

// ---------------------------------------------------------- Trust marquee
export const TrustStrip = () => {
  const items = ["4,9/5 su Google", "200+ recensioni", "Trasporti nazionali", "Assistenza dedicata", "Mezzi propri", "Operatori qualificati"];
  return (
    <div className="bg-[#121212] py-4 overflow-hidden" data-testid="trust-strip">
      <Marquee speed={40} gradient={false} autoFill>
        {items.map((t, i) => (
          <div key={i} className="flex items-center gap-3 mx-8">
            <Star size={16} className="text-[#FF5A00] fill-[#FF5A00]" />
            <span className="text-white/90 font-semibold text-sm uppercase tracking-wider">{t}</span>
          </div>
        ))}
      </Marquee>
    </div>
  );
};

// ---------------------------------------------------------- Services
export const ServicesSection = ({ compact = false }) => (
  <section className="py-24 md:py-32" id="servizi" data-testid="services-section">
    <Container>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
        <Reveal>
          <Overline>I servizi AXAL</Overline>
          <h2 className="font-head text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-3 max-w-xl leading-[1.1]">
            Tutto ciò che serve<br />per muovere le tue cose.
          </h2>
        </Reveal>
        {!compact && (
          <Reveal delay={0.1}>
            <Link to="/servizi" className="inline-flex items-center gap-2 font-bold text-[#0A0A0A] hover:text-[#FF5A00] transition-colors group">
              Scopri tutti i servizi <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        )}
      </div>
      <div className="grid md:grid-cols-3 gap-5">
        {SERVICES.map((s, i) => {
          const Icon = ICONS[s.icon];
          const big = i === 0 || i === 3;
          return (
            <Reveal key={s.key} delay={(i % 3) * 0.08} className={big ? "md:col-span-2" : "md:col-span-1"}>
              <Link to={s.to} data-testid={`service-card-${s.key}`}
                className="group relative block h-72 md:h-80 rounded-[1.5rem] overflow-hidden">
                <img src={s.img} alt={s.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                <div className="absolute top-5 left-5 w-11 h-11 rounded-full bg-white/15 backdrop-blur-md flex items-center justify-center text-white">
                  <Icon size={20} />
                </div>
                <div className="absolute top-5 right-5 w-10 h-10 rounded-full bg-[#FF5A00] flex items-center justify-center text-white opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
                  <ArrowUpRight size={18} />
                </div>
                <div className="absolute bottom-0 left-0 p-6">
                  <h3 className="font-head text-2xl font-bold text-white">{s.title}</h3>
                  <p className="text-white/70 text-sm mt-1.5 max-w-sm leading-relaxed">{s.desc}</p>
                </div>
              </Link>
            </Reveal>
          );
        })}
      </div>
    </Container>
  </section>
);

// ---------------------------------------------------------- Stats + Why
export const WhySection = () => (
  <section className="py-24 md:py-32 bg-[#F4F4F5]" data-testid="why-section">
    <Container>
      <Reveal><Overline>Perché scegliere AXAL</Overline></Reveal>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mt-10 mb-20">
        {STATS.map((s, i) => (
          <Reveal key={i} delay={i * 0.08}>
            <div className="border-l-2 border-[#FF5A00] pl-5">
              <div className="font-head text-5xl md:text-6xl font-extrabold tracking-tight text-[#0A0A0A]">{s.value}</div>
              <div className="text-zinc-500 mt-2 font-medium">{s.label}</div>
            </div>
          </Reveal>
        ))}
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {WHY.map((w, i) => {
          const Icon = ICONS[w.icon];
          return (
            <Reveal key={i} delay={i * 0.08}>
              <div className="bg-white rounded-2xl p-7 h-full hover:shadow-[0_20px_40px_rgb(0,0,0,0.06)] transition-shadow">
                <div className="w-12 h-12 rounded-xl bg-[#FF5A00]/10 flex items-center justify-center text-[#FF5A00]"><Icon size={22} /></div>
                <h3 className="font-head text-lg font-bold mt-5">{w.title}</h3>
                <p className="text-zinc-500 text-[15px] mt-2 leading-relaxed">{w.desc}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </Container>
  </section>
);

// ---------------------------------------------------------- How it works
export const HowSection = () => (
  <section className="py-24 md:py-32" data-testid="how-section">
    <Container>
      <Reveal className="max-w-xl mb-14">
        <Overline>Come funziona</Overline>
        <h2 className="font-head text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-3 leading-[1.1]">Semplice, in quattro passaggi.</h2>
      </Reveal>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
        {STEPS.map((s, i) => (
          <Reveal key={i} delay={i * 0.1}>
            <div className="relative">
              <span className="font-head text-8xl font-extrabold text-zinc-100 leading-none select-none">{s.n}</span>
              <div className="-mt-8 relative">
                <h3 className="font-head text-xl font-bold">{s.title}</h3>
                <p className="text-zinc-500 mt-2 text-[15px] leading-relaxed">{s.desc}</p>
              </div>
              {i < 3 && <ArrowRight className="hidden lg:block absolute top-8 -right-3 text-[#FF5A00]" size={22} />}
            </div>
          </Reveal>
        ))}
      </div>
    </Container>
  </section>
);

// ---------------------------------------------------------- Split feature
export const SplitFeature = ({ overline, title, text, items, img, cta, ctaPreset, reverse, id }) => {
  const { openQuote } = useQuote();
  return (
    <section className="py-20 md:py-28" id={id} data-testid={`split-${id}`}>
      <Container>
        <div className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${reverse ? "lg:[direction:rtl]" : ""}`}>
          <Reveal className="lg:[direction:ltr]">
            <div className="relative rounded-[2rem] overflow-hidden aspect-[4/3]">
              <img src={img} alt={title} className="absolute inset-0 w-full h-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:[direction:ltr]">
            <Overline>{overline}</Overline>
            <h2 className="font-head text-4xl sm:text-5xl font-extrabold tracking-tight mt-3 leading-[1.1]">{title}</h2>
            <p className="text-zinc-500 mt-5 text-lg leading-relaxed max-w-lg">{text}</p>
            {items && (
              <ul className="grid grid-cols-2 gap-3 mt-7 max-w-lg">
                {items.map((it) => (
                  <li key={it} className="flex items-center gap-2.5 text-[15px] font-medium text-zinc-700">
                    <span className="w-5 h-5 rounded-full bg-[#FF5A00]/10 flex items-center justify-center shrink-0"><Check size={13} className="text-[#FF5A00]" /></span>
                    {it}
                  </li>
                ))}
              </ul>
            )}
            <button onClick={() => openQuote(ctaPreset)} data-testid={`split-cta-${id}`}
              className="mt-8 h-13 py-4 px-7 rounded-full bg-[#0A0A0A] text-white font-bold inline-flex items-center gap-2 hover:bg-[#FF5A00] transition-colors">
              {cta} <ArrowRight size={18} />
            </button>
          </Reveal>
        </div>
      </Container>
    </section>
  );
};

// ---------------------------------------------------------- Shipping options
const SHIP_OPTIONS = [
  { key: "standard", icon: Truck, name: "Servizio Standard", time: "15/18", unit: "giorni lavorativi", price: "Preventivo standard", desc: "Ritiro e consegna nell'arco di circa 15/18 giorni lavorativi." },
  { key: "espresso", icon: Zap, name: "Servizio Espresso", time: "12/13", unit: "giorni lavorativi", price: "+25% sul preventivo standard", desc: "Ritiro e consegna entro circa 12/13 giorni lavorativi." },
  { key: "urgente", icon: Rocket, name: "Servizio Urgente", time: "7/8", unit: "giorni lavorativi", price: "+40% sul preventivo standard", desc: "Ritiro e consegna entro circa 7/8 giorni lavorativi." },
];

export const ShippingOptions = ({ servizio = "trasporto" }) => {
  const { openQuote } = useQuote();
  return (
    <section className="py-24 md:py-32 bg-[#F4F4F5]" data-testid="shipping-options">
      <Container>
        <Reveal className="max-w-2xl mb-12">
          <Overline>Tempi di consegna</Overline>
          <h2 className="font-head text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-3 leading-[1.1]">Scegli la velocità della tua spedizione.</h2>
          <p className="text-zinc-500 mt-5 text-lg leading-relaxed">Le opzioni Espresso e Urgente sono disponibili previa verifica della fattibilità logistica.</p>
        </Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {SHIP_OPTIONS.map((o, i) => {
            const Icon = o.icon;
            return (
              <Reveal key={o.key} delay={i * 0.08}>
                <div className="bg-white rounded-2xl p-7 h-full flex flex-col border border-zinc-100 hover:shadow-[0_20px_40px_rgb(0,0,0,0.06)] transition-shadow" data-testid={`ship-option-${o.key}`}>
                  <div className="w-12 h-12 rounded-xl bg-[#FF5A00]/10 flex items-center justify-center text-[#FF5A00]"><Icon size={22} /></div>
                  <h3 className="font-head text-xl font-bold mt-5">{o.name}</h3>
                  <div className="mt-4 flex items-baseline gap-2">
                    <span className="font-head text-5xl font-extrabold tracking-tight">{o.time}</span>
                    <span className="text-zinc-500 text-sm font-medium">{o.unit}</span>
                  </div>
                  <p className="text-zinc-500 text-[15px] mt-3 leading-relaxed">{o.desc}</p>
                  <p className="mt-4 text-sm font-bold text-[#FF5A00]">{o.price}</p>
                  <button onClick={() => openQuote({ servizio, messaggio: `Richiesta ${o.name}` })} data-testid={`ship-option-${o.key}-cta`}
                    className="mt-auto pt-6 inline-flex items-center gap-2 font-bold text-[#0A0A0A] hover:text-[#FF5A00] transition-colors group text-left">
                    Richiedi preventivo <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </Reveal>
            );
          })}
          <Reveal delay={0.24}>
            <div className="bg-[#121212] text-white rounded-2xl p-7 h-full flex flex-col" data-testid="ship-option-dedicato">
              <div className="w-12 h-12 rounded-xl bg-[#FF5A00] flex items-center justify-center"><Route size={22} /></div>
              <h3 className="font-head text-xl font-bold mt-5">Trasporto Dedicato</h3>
              <div className="mt-4 font-head text-3xl font-extrabold tracking-tight">Su richiesta</div>
              <p className="text-zinc-400 text-[15px] mt-3 leading-relaxed">Un mezzo riservato solo alla tua spedizione, con data e orario concordati. Tempi e costi valutati caso per caso.</p>
              <button onClick={() => openQuote({ servizio, messaggio: "Richiesta Trasporto Dedicato" })} data-testid="ship-option-dedicato-cta"
                className="mt-auto pt-6 inline-flex items-center gap-2 font-bold text-[#FF5A00] hover:text-white transition-colors group text-left">
                Richiedi un dedicato <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </Reveal>
        </div>
      </Container>
    </section>
  );
};

// ---------------------------------------------------------- Business
export const BusinessSection = () => {
  const { openQuote } = useQuote();
  return (
    <section className="py-24 md:py-32 bg-[#121212] text-white relative overflow-hidden" data-testid="business-section">
      <div className="absolute inset-0 noise-overlay opacity-[0.05]" />
      <Container className="relative">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <Overline>AXAL Business</Overline>
            <h2 className="font-head text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-3 leading-[1.1]">Logistica e trasporti per aziende e professionisti.</h2>
            <button onClick={() => openQuote({ servizio: "business" })} data-testid="business-cta"
              className="mt-8 h-13 py-4 px-7 rounded-full bg-[#FF5A00] text-white font-bold inline-flex items-center gap-2 hover:bg-[#E04F00] transition-colors">
              Scopri AXAL Business <ArrowRight size={18} />
            </button>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex flex-wrap gap-3">
              {BUSINESS_SECTORS.map((s, i) => (
                <span key={i} className="px-5 py-3 rounded-full border border-white/15 text-white/80 font-medium capitalize hover:border-[#FF5A00] hover:text-white transition-colors">{s}</span>
              ))}
            </div>
          </Reveal>
        </div>
      </Container>
    </section>
  );
};

// ---------------------------------------------------------- Reviews
export const ReviewsSection = () => (
  <section className="py-24 md:py-32" id="recensioni" data-testid="reviews-section">
    <Container>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
        <Reveal>
          <Overline>Recensioni Google</Overline>
          <div className="flex items-center gap-4 mt-4">
            <span className="font-head text-6xl font-extrabold tracking-tight">4,9</span>
            <div>
              <div className="flex gap-1">{[...Array(5)].map((_, i) => <Star key={i} size={20} className="text-[#FF5A00] fill-[#FF5A00]" />)}</div>
              <p className="text-zinc-500 mt-1 text-sm">Oltre 200 clienti hanno recensito AXAL</p>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="flex flex-wrap items-center gap-5">
            <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer" data-testid="reviews-google-link"
              className="h-12 px-6 rounded-full bg-[#0A0A0A] text-white font-bold inline-flex items-center gap-2 hover:bg-[#FF5A00] transition-colors">
              Vedi su Google <ArrowUpRight size={18} />
            </a>
            <Link to="/recensioni" data-testid="reviews-all-link" className="inline-flex items-center gap-2 font-bold hover:text-[#FF5A00] transition-colors group">
              Leggi tutte le recensioni <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </Reveal>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {REVIEWS.slice(0, 6).map((r, i) => (
          <Reveal key={i} delay={(i % 3) * 0.08}>
            <div className="bg-white border border-zinc-100 rounded-2xl p-7 h-full shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <div className="flex gap-1 mb-4">{[...Array(r.rating)].map((_, j) => <Star key={j} size={16} className="text-[#FF5A00] fill-[#FF5A00]" />)}</div>
              <p className="text-zinc-700 leading-relaxed text-[15px]">"{r.text}"</p>
              <div className="flex items-center gap-3 mt-6">
                <div className="w-10 h-10 rounded-full bg-[#FF5A00]/10 flex items-center justify-center font-head font-bold text-[#FF5A00]">{r.name[0]}</div>
                <div>
                  <div className="font-bold text-sm">{r.name}</div>
                  <div className="text-zinc-400 text-xs">{r.city} · Google</div>
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </Container>
  </section>
);

// ---------------------------------------------------------- Gallery
export const GallerySection = () => {
  const CATS = ["Traslochi", "Moto", "Trasporti", "Autoscala", "Dietro le quinte"];
  const imgs = [IMAGES.traslochi, IMAGES.moto, IMAGES.trasporti, IMAGES.autoscala, IMAGES.warehouse, IMAGES.movers, IMAGES.heroFleet, IMAGES.spedizioni];
  return (
    <section className="py-24 md:py-32 bg-[#F4F4F5]" data-testid="gallery-section">
      <Container>
        <Reveal className="mb-10">
          <Overline>I nostri trasporti</Overline>
          <h2 className="font-head text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-3 leading-[1.1]">Lavori reali. Mezzi propri.</h2>
        </Reveal>
        <div className="flex flex-wrap gap-2 mb-8">
          {CATS.map((c) => <span key={c} className="px-4 py-2 rounded-full bg-white text-sm font-semibold text-zinc-600 border border-zinc-200">{c}</span>)}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {imgs.map((src, i) => (
            <Reveal key={i} delay={(i % 4) * 0.06} className={i === 0 || i === 5 ? "col-span-2 row-span-1" : ""}>
              <div className="group relative rounded-2xl overflow-hidden aspect-square h-full">
                <img src={src} alt="AXAL lavoro" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
              </div>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
};

// ---------------------------------------------------------- Coverage map
const CITIES = {
  Milano: { x: 32, y: 20 }, Torino: { x: 18, y: 22 }, Bologna: { x: 43, y: 33 },
  Firenze: { x: 44, y: 42 }, Roma: { x: 50, y: 58 }, Napoli: { x: 60, y: 68 },
  Bari: { x: 76, y: 68 }, Palermo: { x: 56, y: 90 },
};
export const CoverageMap = () => (
  <section className="py-24 md:py-32 bg-[#121212] text-white relative overflow-hidden" data-testid="coverage-section">
    <div className="absolute inset-0 noise-overlay opacity-[0.05]" />
    <Container className="relative">
      <div className="grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <Overline>Copertura nazionale</Overline>
          <h2 className="font-head text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-3 leading-[1.1]">Da Roma verso tutta Italia.</h2>
          <p className="text-zinc-400 mt-5 text-lg leading-relaxed max-w-md">Organizziamo quotidianamente trasporti e spedizioni sulle principali tratte nazionali.</p>
          <div className="flex flex-wrap gap-2.5 mt-8">
            {ROUTES_LIST.map((r) => (
              <span key={r} className="px-4 py-2.5 rounded-full border border-white/15 text-sm font-semibold text-white/80 hover:border-[#FF5A00] hover:text-white transition-colors">{r}</span>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="relative aspect-square max-w-md mx-auto">
            <svg viewBox="0 0 100 100" className="w-full h-full">
              {Object.entries(CITIES).filter(([n]) => n !== "Roma").map(([n, c], i) => (
                <motion.line key={n} x1={CITIES.Roma.x} y1={CITIES.Roma.y} x2={c.x} y2={c.y}
                  stroke="#FF5A00" strokeWidth="0.5" strokeDasharray="2 2"
                  initial={{ pathLength: 0, opacity: 0 }} whileInView={{ pathLength: 1, opacity: 0.7 }}
                  viewport={{ once: true }} transition={{ duration: 1.2, delay: 0.2 + i * 0.15 }} />
              ))}
              {Object.entries(CITIES).map(([n, c]) => {
                const isRome = n === "Roma";
                return (
                  <g key={n}>
                    <motion.circle cx={c.x} cy={c.y} r={isRome ? 2.2 : 1.4} fill={isRome ? "#FF5A00" : "#fff"}
                      initial={{ scale: 0 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ delay: 0.3, type: "spring" }} />
                    {isRome && <motion.circle cx={c.x} cy={c.y} r="2.2" fill="none" stroke="#FF5A00" strokeWidth="0.4"
                      initial={{ r: 2.2, opacity: 0.8 }}
                      animate={{ r: [2.2, 6], opacity: [0.8, 0] }} transition={{ duration: 2, repeat: Infinity }} />}
                    <text x={c.x + 2.5} y={c.y + 1} fill={isRome ? "#FF5A00" : "#a1a1aa"} fontSize="3" fontWeight="600">{n}</text>
                  </g>
                );
              })}
            </svg>
          </div>
        </Reveal>
      </div>
    </Container>
  </section>
);

// ---------------------------------------------------------- Dark CTA
export const DarkCTA = () => {
  const { openQuote } = useQuote();
  return (
    <section className="relative py-28 md:py-40 overflow-hidden" data-testid="dark-cta">
      <img src={IMAGES.heroTruck} alt="AXAL" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-[#0A0A0A]/85" />
      <div className="absolute inset-0 noise-overlay opacity-[0.06]" />
      <Container className="relative text-center">
        <Reveal>
          <h2 className="font-head text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tight text-white leading-[1.05]">
            Hai qualcosa da trasportare?<br /><span className="text-[#FF5A00]">Pensiamo noi al resto.</span>
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
            <button onClick={() => openQuote()} data-testid="cta-quote"
              className="h-14 px-8 rounded-full bg-[#FF5A00] text-white font-bold text-lg hover:bg-[#E04F00] transition-colors shadow-[0_0_40px_rgba(255,90,0,0.4)]">
              Richiedi un preventivo
            </button>
            <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="cta-whatsapp"
              className="h-14 px-8 rounded-full border border-white/25 text-white font-bold text-lg flex items-center justify-center hover:bg-white/10 transition-colors">
              WhatsApp AXAL
            </a>
          </div>
        </Reveal>
      </Container>
    </section>
  );
};

// ---------------------------------------------------------- Page hero (inner pages)
export const PageHero = ({ overline, title, subtitle, img }) => (
  <section className="relative pt-32 md:pt-44 pb-20 md:pb-28 bg-[#121212] text-white overflow-hidden" data-testid="page-hero">
    {img && <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />}
    <div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-[#121212]/70 to-[#121212]/50" />
    <Container className="relative">
      <Reveal><Overline>{overline}</Overline></Reveal>
      <Reveal delay={0.08}>
        <h1 className="font-head text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight mt-4 leading-[1.05] max-w-4xl">{title}</h1>
      </Reveal>
      {subtitle && <Reveal delay={0.16}><p className="text-zinc-300 text-lg mt-6 max-w-2xl leading-relaxed">{subtitle}</p></Reveal>}
    </Container>
  </section>
);

export { Container, Overline };
